import React from "react";
import Modal from "react-bootstrap/Modal";
import PropTypes from "prop-types";

/**
 * EditModal — the shared edit-modal shell for profile sections.
 *
 * A thin react-bootstrap Modal wrapper: a titled header with a close button, then the engine's
 * body rendered raw. The engine owns everything domain-specific — the section→form-body mapping,
 * the title text, and the open/close state all flow in as props.
 *
 * Body contract: the shell renders only the `<Modal>` + `<Modal.Header>` chrome; the *body* slot
 * is responsible for its own `<Modal.Body>` and (optionally) `<Modal.Footer>`. Both engine
 * families already do this (Company's edit forms and PR's modal bodies), so the shell does NOT
 * wrap `children` in a `<Modal.Body>` — doing so would nest a `<Modal.Body>` inside a
 * `<Modal.Body>` and push the footer inside the body. This shell only standardizes the chrome
 * (and accessibility/focus handling via react-bootstrap).
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onClose - Called when the modal is dismissed (close button / backdrop / esc)
 * @param {React.ReactNode} props.title - Modal title content
 * @param {React.ReactNode} props.children - The body for the active section (owns its Modal.Body/Footer)
 * @param {string} [props.size] - react-bootstrap modal size (default "lg")
 * @param {boolean|string} [props.fullscreen] - react-bootstrap `fullscreen` (e.g. "lg-down" for mobile)
 */
function EditModal({ show, onClose, title, children, size = "lg", fullscreen = undefined }) {
  return (
    <Modal show={show} onHide={onClose} size={size} fullscreen={fullscreen}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      {children}
    </Modal>
  );
}

EditModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node,
  size: PropTypes.string,
  fullscreen: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default EditModal;
